<template>
  <router-view
    v-if="!selfTestStore.needsRestart"
    :progress="displayProgress"
    :can-go-back="canGoBack"
    :handle-next-event="goToNextOrCompleteQuestionnaire"
    @back="goToPreviousRoute"
  />
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'
import { computed, onUnmounted } from 'vue'
import env from '@shared/env.js'
import useAssessmentStore from '@shared/modules/assessment/stores/useAssessmentStore.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import useUserStore from '@shared/stores/useUserStore'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import {
  MSK_PRE_SIGNUP_STEPS,
  MSK_PRE_SUBSCRIPTION_STEPS,
  MSK_EU_POST_ACTIVATION_STEPS,
  MSK_EU_FULL_STEPS,
  MSK_FULL_STEPS,
  COPD_STEPS,
} from '@shared/modules/assessment/router/selfTestStepSets.js'

const getSelfTestSteps = ({
  disease,
  isMskEu,
  isAuthenticated,
  hasCurrentSubscription,
  hasSelfTestGoal,
}) => {
  if (disease === 'copd') return COPD_STEPS
  if (disease !== 'msk') return []

  if (isMskEu) {
    // Before sign-up the router hook only admits web-funnel users here; after
    // sign-in without a subscription this is the in-app patient without a
    // prescription, before doctor outreach.
    if (!isAuthenticated || !hasCurrentSubscription) return MSK_PRE_SIGNUP_STEPS
    // A stored goal means the pre-activation set was answered.
    return hasSelfTestGoal ? MSK_EU_POST_ACTIVATION_STEPS : MSK_EU_FULL_STEPS
  }

  return hasCurrentSubscription ? MSK_FULL_STEPS : MSK_PRE_SUBSCRIPTION_STEPS
}

const route = useRoute()
const router = useRouter()
const assessmentStore = useAssessmentStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const selfTestStore = useSelfTestStore()

const steps = computed(() =>
  getSelfTestSteps({
    disease: env.VITE_DISEASE,
    isMskEu: env.isMskEu,
    isAuthenticated: authStore.isAuthenticated,
    hasCurrentSubscription: userStore.user?.currentSubscription != null,
    hasSelfTestGoal: userStore.user?.profile?.hasSelfTestGoal === true,
  }),
)

// Do not mount a stale completion step: it can submit automatically on mount.
if (selfTestStore.needsRestart) {
  // A guard can leave the assessment; keep its restart pending in that case.
  let restartActive = true
  onUnmounted(() => {
    restartActive = false
  })
  const firstStep = steps.value[0]
  if (route.name === firstStep) {
    selfTestStore.needsRestart = false
  } else {
    router.replace({ name: firstStep, query: route.query }).then((failure) => {
      if (!failure && restartActive) selfTestStore.needsRestart = false
    })
  }
}

const currentIndex = computed(() =>
  steps.value.findIndex((step) => step === route.name),
)

const progressFraction = computed(() => {
  if (currentIndex.value < 0) return null
  if (steps.value.length <= 1) return 1

  return currentIndex.value / (steps.value.length - 1)
})

const displayProgress = computed(() => {
  if (currentIndex.value < 0) {
    return null
  }

  const range = assessmentStore.onboardingProgressRange

  if (range == null || progressFraction.value == null) {
    return progressFraction.value
  }

  const { startProgress, endProgress } = range
  const progressSpan = endProgress - startProgress
  const rangedProgressFraction =
    (currentIndex.value + 1) / (steps.value.length + 1)

  return startProgress + progressSpan * rangedProgressFraction
})

const canGoBack = computed(() => currentIndex.value > 0)

const getRouteProperties = (modifier) => {
  const index = currentIndex.value + modifier

  if (typeof index !== 'number' || index < 0 || index >= steps.value.length) {
    throw new Error(
      `selfTestNavigation Mixin: Route cannot be determined because the target index is out of bounds. Current route name: ${route.name}, modifier: ${modifier}, target index: ${index}`,
    )
  }

  return { name: steps.value[index] }
}

const goToNextOrCompleteQuestionnaire = async () => {
  try {
    let routeProperties = getRouteProperties(+1)
    await router.push(routeProperties)
  } catch {
    // complete questionnaires;
    await assessmentStore.completeQuestionnaire(router)
  }
}

const goToPreviousRoute = () => {
  if (currentIndex.value > 0) {
    router.push({
      name: steps.value[currentIndex.value - 1],
      query: route.query,
    })
  }
}

onUnmounted(() => {
  if (assessmentStore.embeddedFlowContext == null) return
  // Keep the context alive if the pre-signup assessment was completed so that
  // navigating back into the flow and pressing "next" on the complete screen
  // still routes correctly to the finish route (e.g. insurance-search).
  if (selfTestStore.hasCompletedPreSignupAssessment) return

  assessmentStore.resetEmbeddedFlowContext()
})
</script>
